'use client';

import { EnhancedSearchFilter } from '@/components/enhanced-search-filter';
import { InfiniteScrollTrigger } from '@/components/infinite-scroll-trigger';
import { QuestionListSkeleton } from '@/components/question-list-skeleton';
import { RichTextContentViewer } from '@/components/rich-text-viewer';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { cn, getRootLink } from '@/lib/utils';
import { SlugRoutes } from '@/types/api/common';
import type { QuestionInfoType } from '@/types/api/idea-starter';
import type { StoryInfoType } from '@/types/api/stories';
import { format } from 'date-fns';
import { BookOpen, Filter, HelpCircle, Search, X } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

interface UnifiedResultsListProps {
  questions: QuestionInfoType[];
  stories: (StoryInfoType & { type: 'story' })[];
  isLoading?: boolean;
  hideUserInfo?: boolean;
  hideReport?: boolean;
  slugRoute?: SlugRoutes;
  // New props for infinite scrolling
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage?: () => void;
  isInfiniteScroll?: boolean;
  hideAddStoryAction?: boolean;
  isActivity?: boolean;
  // New props for integrated search
  onSearch?: (searchQuery: string, filters: any) => void;
  showSearchComponent?: boolean;
  initialSearchQuery?: string;
  initialFilters?: any;
}

// Type guard to check if item is a story
const isStoryResult = (
  item: QuestionInfoType | (StoryInfoType & { type: 'story' })
): item is StoryInfoType & { type: 'story' } => {
  return 'type' in item && item.type === 'story';
};

// Date formatting utility
const formatDate = (dateString: string) => {
  try {
    return format(new Date(dateString), 'dd MMM yyyy');
  } catch {
    return 'Unknown date';
  }
};

// HTML tag stripping utility
const stripHtmlTags = (html: string) => {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').trim();
};

// Build a short, plain-text preview from rich HTML using stripHtmlTags
const buildDescriptionPreview = (
  html: string | null | undefined,
  maxLength = 200
): string => {
  if (!html) return '';

  // 1) Decode common HTML entities so encoded tags become real tags
  const decoded = html
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'");

  // 2) Strip ALL HTML tags (including <a> and its attributes) using stripHtmlTags
  let text = stripHtmlTags(decoded);
  if (!text) return '';

  // 3) Remove bare URLs from the plain text (http/https/www and generic ://)
  text = text
    .replace(/https?:\/\/\S+/gi, '')
    .replace(/\bwww\.\S+/gi, '')
    .replace(/\S*:\/\/\S*/gi, '');

  // 4) Normalize whitespace
  text = text.replace(/\s+/g, ' ').trim();
  if (!text) return '';

  // Truncate to maxLength, cutting at a word boundary
  if (text.length <= maxLength) return text;
  const truncated = text.slice(0, maxLength);
  const lastSpace = truncated.lastIndexOf(' ');
  const safeCut = lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated;
  return safeCut + '…';
};

// Integrated Search Component for Results Page Only
const IntegratedSearchComponent = ({
  onSearch,
  initialSearchQuery = '',
  initialFilters = {},
}: {
  onSearch: (searchQuery: string, filters: any) => void;
  initialSearchQuery?: string;
  initialFilters?: any;
}) => {
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentSearchTerms, setCurrentSearchTerms] = useState<string[]>([]);
  const [selectedFilters, setSelectedFilters] = useState({
    contentType: 'all',
    questionTypes: [],
    areaOfInterestIds: [],
    ...initialFilters,
  });

  // Initialize current search terms from initial query (from navbar "view all results")
  useEffect(() => {
    if (initialSearchQuery && initialSearchQuery.trim()) {
      // Split initial query into individual terms for multi-keyword support
      const terms = initialSearchQuery
        .trim()
        .split(/\s+/)
        .filter((term) => term.length > 0);
      setCurrentSearchTerms(terms);
    } else {
      // Clear current search terms if initialSearchQuery is empty
      setCurrentSearchTerms([]);
    }
  }, [initialSearchQuery]);

  // Update filters when initialFilters change
  useEffect(() => {
    setSelectedFilters((prev: any) => ({
      ...prev,
      ...initialFilters,
    }));
  }, [initialFilters]);

  // Clear search input when current search terms are cleared
  useEffect(() => {
    if (currentSearchTerms.length === 0) {
      setSearchQuery('');
    }
  }, [currentSearchTerms]);

  const handleSearch = () => {
    if (searchQuery.trim()) {
      const newSearchTerm = searchQuery.trim();

      // Replace existing search terms with the new one (as per user requirement)
      setCurrentSearchTerms([newSearchTerm]);

      // Perform search with the new search term
      if (onSearch) {
        onSearch(newSearchTerm, {
          ...selectedFilters,
          searchTerm: newSearchTerm,
        });
      }

      // Clear the input field
      setSearchQuery('');
    } else if (currentSearchTerms.length > 0) {
      // If no new query but search terms exist, search with existing search terms
      const combinedQuery = currentSearchTerms.join(' ');
      if (onSearch) {
        onSearch(combinedQuery, {
          ...selectedFilters,
          searchTerm: combinedQuery,
        });
      }
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const removeSearchTerm = (termToRemove?: string) => {
    if (termToRemove) {
      // Remove specific search term
      setCurrentSearchTerms((prev) => {
        const updatedTerms = prev.filter((term) => term !== termToRemove);

        // Perform search with remaining terms
        const combinedQuery = updatedTerms.join(' ');
        if (onSearch) {
          onSearch(combinedQuery, {
            ...selectedFilters,
            searchTerm: combinedQuery,
          });
        }

        // Update URL if no terms remain
        if (updatedTerms.length === 0) {
          router.push('/search');
        }

        return updatedTerms;
      });
    } else {
      // Clear all search terms
      setCurrentSearchTerms([]);
      setSearchQuery('');

      // Update URL to remove search query parameter
      router.push('/search');

      // Show all results (clear the search)
      if (onSearch) {
        onSearch('', { ...selectedFilters, searchTerm: '' });
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-4 lg:p-6 mb-6 w-full overflow-hidden">
      {/* Search Bar with Multiple Search Term Badges */}
      <div className="space-y-3 w-full">
        {/* Search Input Row */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full">
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-5 w-5" />
            <Input
              type="text"
              placeholder={
                currentSearchTerms.length > 0
                  ? 'Enter new search term to replace current...'
                  : 'Search questions and stories...'
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              className="pl-10 pr-10 h-12 text-lg w-full"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
          <Button
            onClick={handleSearch}
            variant="default"
            size="lg"
            disabled={!searchQuery.trim() && currentSearchTerms.length === 0}
            className="whitespace-nowrap"
          >
            Search
          </Button>
        </div>

        {/* Multiple Search Terms Badge Display */}
        {currentSearchTerms.length > 0 && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full">
            <span className="text-sm text-gray-600 font-medium whitespace-nowrap">
              Search:
            </span>
            <div className="flex flex-wrap gap-2 min-w-0">
              {currentSearchTerms.map((term, index) => (
                <Badge
                  key={`${term}-${index}`}
                  variant="secondary"
                  className="bg-blue-100 text-blue-800 hover:bg-blue-200 cursor-pointer px-3 py-1 flex items-center gap-1 whitespace-nowrap"
                  onClick={() => removeSearchTerm(term)}
                >
                  <span className="text-sm">&ldquo;{term}&rdquo;</span>
                  <X className="h-3 w-3" />
                </Badge>
              ))}
              {currentSearchTerms.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeSearchTerm()}
                  className="text-gray-500 hover:text-gray-700 text-xs whitespace-nowrap"
                >
                  Clear all
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Story Card Component - Redesigned with content priority
const StoryCard = ({
  story,
  hideUserInfo = false,
  hideReport = false,
}: {
  story: StoryInfoType & { type: 'story' };
  hideUserInfo?: boolean;
  hideReport?: boolean;
}) => {
  return (
    <Link
      href={`/home/question/${story.question?.slug || 'unknown'}?focusedStoryId=${story.id}`}
    >
      <Card className="shadow-md rounded-lg p-4 hover:shadow-lg transition-shadow duration-200 w-full overflow-hidden">
        <CardContent className="px-0">
          <div className="flex flex-col gap-3 w-full">
            {/* Badge and Headline Row */}
            <div className="flex items-start gap-3 w-full">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                  <BookOpen className="h-4 w-4 text-gray-700" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 text-wrap break-words">
                  {stripHtmlTags(
                    story.title || story.theme || 'Untitled Story'
                  )}
                </h3>
              </div>
            </div>

            {/* Theme - Separate Line */}
            {story.theme && (
              <div className="flex gap-3 w-full">
                <div className="flex-shrink-0 w-8"></div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-700 break-words">
                    {stripHtmlTags(story.theme)}
                  </div>
                </div>
              </div>
            )}

            {/* Types and Tags - Chip Style */}
            <div className="flex gap-3 w-full">
              <div className="flex-shrink-0 w-8"></div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap gap-1">
                  {story.keywords &&
                    story.keywords.length > 0 &&
                    story.keywords.map((keyword, index) => (
                      <Badge
                        key={index}
                        variant="outline"
                        className="text-xs bg-gray-50"
                      >
                        {keyword}
                      </Badge>
                    ))}
                </div>
              </div>
            </div>

            {/* Metadata Row - Date and User (Small) */}
            <div className="flex gap-3 w-full">
              <div className="flex-shrink-0 w-8"></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-end text-xs text-gray-500 gap-4">
                  <span>Created: {formatDate(story.createdDate)}</span>
                  {!hideUserInfo && story.author && (
                    <div className="flex items-center gap-2">
                      <span>By:</span>
                      <div className="flex items-center gap-1">
                        <Avatar className="h-4 w-4">
                          <AvatarImage
                            src={
                              story.isAnonymous
                                ? '/anonymous.svg'
                                : story.author.avatarUrl
                            }
                          />
                          <AvatarFallback className="text-xs">
                            {story.isAnonymous
                              ? 'A'
                              : story.author.fullName?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-xs">
                          {story.isAnonymous
                            ? 'Anonymous'
                            : story.author.fullName || 'Unknown'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

// Question Card Component - Redesigned with content priority
const QuestionCard = ({
  question,
  hideUserInfo = false,
  hideReport = false,
  slugRoute = 'home',
  hideAddStoryAction = false,
  isActivity = false,
}: {
  question: QuestionInfoType;
  hideUserInfo?: boolean;
  hideReport?: boolean;
  slugRoute?: SlugRoutes;
  hideAddStoryAction?: boolean;
  isActivity?: boolean;
}) => {
  return (
    <Link href={`/${slugRoute}/question/${question.slug}`}>
      <Card className="shadow-md rounded-lg p-4 hover:shadow-lg transition-shadow duration-200 w-full overflow-hidden">
        <CardContent className="px-0">
          <div className="flex flex-col gap-3 w-full">
            {/* Badge and Title Row */}
            <div className="flex items-start gap-3 w-full">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                  <HelpCircle className="h-4 w-4 text-gray-700" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 line-clamp-2 text-wrap break-words">
                  {stripHtmlTags(question.questionTitle)}
                </h3>
              </div>
            </div>

            {/* Description - show full HTML description with links */}
            {question.questionDescription && (
              <div className="flex gap-3 w-full">
                <div className="flex-shrink-0 w-8"></div>
                <div className="flex-1 min-w-0">
                  <div
                    className="text-sm text-gray-700 break-words"
                    dangerouslySetInnerHTML={{
                      __html: question.questionDescription || '',
                    }}
                  />
                </div>
              </div>
            )}

            {/* Types and Tags - Chip Style */}
            <div className="flex gap-3 w-full">
              <div className="flex-shrink-0 w-8"></div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap gap-1">
                  {question.questionType === 'IDEA_STARTER' && (
                    <Badge
                      variant="default"
                      className="text-xs bg-ihs-dark-blue/20 text-ihs-dark-blue border border-ihs-dark-blue/30"
                    >
                      Idea Starter
                    </Badge>
                  )}
                  {question.category && (
                    <Badge variant="outline" className="text-xs bg-gray-50">
                      {question.category.name}
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Metadata Row - Date and User (Small) */}
            <div className="flex gap-3 w-full">
              <div className="flex-shrink-0 w-8"></div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-end text-xs text-gray-500 gap-4">
                  <span>Created: {formatDate(question.createdDate)}</span>
                  {!hideUserInfo && question.author && (
                    <div className="flex items-center gap-2">
                      <span>By:</span>
                      <div className="flex items-center gap-1">
                        <Avatar className="h-4 w-4">
                          <AvatarImage
                            src={
                              question.isAnonymous
                                ? '/anonymous.svg'
                                : question.author.avatarUrl
                            }
                          />
                          <AvatarFallback className="text-xs">
                            {question.isAnonymous
                              ? 'A'
                              : question.author.fullName?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-xs">
                          {question.isAnonymous
                            ? 'Anonymous'
                            : question.author.fullName || 'Unknown'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export const UnifiedResultsList = ({
  questions,
  stories,
  isLoading,
  hideUserInfo = false,
  hideReport = false,
  slugRoute = 'home',
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage,
  isInfiniteScroll = false,
  hideAddStoryAction = false,
  isActivity = false,
  onSearch,
  showSearchComponent = false,
  initialSearchQuery = '',
  initialFilters = {},
}: UnifiedResultsListProps) => {
  // Set up infinite scroll if enabled
  const { setTargetRef } = useInfiniteScroll({
    onIntersect: () => {
      if (fetchNextPage && hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    },
    enabled: isInfiniteScroll && !!fetchNextPage && hasNextPage,
  });

  // Combine and sort results by relevance (questions first, then stories)
  const allResults = [
    ...questions.map((q) => ({ ...q, _type: 'question' as const })),
    ...stories.map((s) => ({ ...s, _type: 'story' as const })),
  ];

  // Deduplicate by composite key to avoid React key collisions across pages/merges
  const seenKeys = new Set<string>();
  const dedupedResults = allResults.filter((item) => {
    const compositeKey = `${item._type}-${item.id}`;
    if (seenKeys.has(compositeKey)) {
      console.warn('UnifiedResultsList duplicate dropped:', compositeKey, item);
      return false;
    }
    seenKeys.add(compositeKey);
    return true;
  });

  return (
    <div className="w-full">
      {/* Integrated Search Component - Only for Results Page */}
      {showSearchComponent && onSearch && (
        <IntegratedSearchComponent
          onSearch={onSearch}
          initialSearchQuery={initialSearchQuery}
          initialFilters={initialFilters}
        />
      )}

      {/* Results List */}
      <div className={cn('flex flex-col gap-4', hideAddStoryAction && 'gap-0')}>
        {isLoading && allResults.length === 0 ? (
          <QuestionListSkeleton />
        ) : (
          dedupedResults.map((item) => {
            if (isStoryResult(item)) {
              return (
                <StoryCard
                  key={`story-${item.id}`}
                  story={item}
                  hideUserInfo={hideUserInfo}
                  hideReport={hideReport}
                />
              );
            } else {
              return (
                <QuestionCard
                  key={`question-${item.id}`}
                  question={item}
                  hideUserInfo={hideUserInfo}
                  hideReport={hideReport}
                  slugRoute={getRootLink(item.questionType)}
                  hideAddStoryAction={hideAddStoryAction}
                  isActivity={isActivity}
                />
              );
            }
          })
        )}

        {/* Render the infinite scroll trigger only if infinite scroll is enabled */}
        {isInfiniteScroll && (
          <InfiniteScrollTrigger
            isLoading={isFetchingNextPage}
            hasNextPage={hasNextPage}
            setTargetRef={setTargetRef}
          />
        )}
      </div>
    </div>
  );
};
